import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useStatsigClient } from '@statsig/react-bindings';

import {
  ArrowsLeftRightIcon,
  BarStackChart,
  Button,
  formatData,
  getGridColorsForCount,
  Layout,
  Legend,
  Tabs,
  tooltipFormatter,
  truncateTextWithEllipsis,
  useLocalStorage,
} from '@utilities';

import { GATES } from '@constants';

import { indexToColumnLetter, statThresholds } from '@utilities/helpers/significance';

import { DISPLAY_AS } from '../../utilities/helpers';
import GridTable from '../GridTable';
import NoDataPlaceholder from '../NoDataPlaceholder';
import SignificanceControls from '../SignificanceControls';

import { applyGridSignificanceColumns } from './utilities/applyGridSignificanceColumns';
import {
  computeGridSignificance,
  computeMonadicGridSignificance,
} from './utilities/gridSignificanceAdapter';

import styles from './_index.module.scss';

const Grid = ({
  compareByGroups,
  data,
  displayAs = DISPLAY_AS.PERCENT.value,
  initialSignificanceAlpha,
  isAskWhySurvey = false,
  language,
  onSignificanceChange,
  onTop2BoxApplied,
  question,
  selectedCompareBy,
  significanceAvailable = false,
  surveyId,
}) => {
  const [areColorsReversed, setAreColorsReversed] = useLocalStorage(
    `survey-${surveyId}-${question.id}-colors-reversed`,
    false
  );
  const [selectedCompareByTab, setSelectedCompareByTab] = useState();
  const [significanceAlpha, setSignificanceAlpha] = useState(initialSignificanceAlpha);

  const { client } = useStatsigClient();
  const significanceEnabled = client.checkGate(GATES.SURVEY_RESPONSES_SIGNIFICANCE);
  const showSignificanceLetters = significanceAvailable && significanceAlpha != null;

  const handleSignificanceChange = (nextAlpha) => {
    setSignificanceAlpha(nextAlpha ?? undefined);
    onSignificanceChange?.(nextAlpha);
  };

  const isDisplayAsPercent = displayAs === DISPLAY_AS.PERCENT.value;
  const isMonadicSectionQuestion = Boolean(question?.concepts?.length);
  const isOverallCompareBy = selectedCompareBy?.value === 'overall';
  // sequential-monadic grid responses arrive as per-compare-by-group slices
  const isCompareBySegmented = !isOverallCompareBy && Boolean(data?.dataByCompareBy);
  const chartKeyToDisplay = isDisplayAsPercent ? 'chartPct' : 'chartCount';
  const compareByTabs = useMemo(() => {
    if (!isCompareBySegmented) return [];

    return (
      compareByGroups
        ?.filter(({ id, name }) => id != null || name != null)
        .map(({ id, name }) => ({
          label: name ?? id,
          value: String(id ?? name),
        })) || []
    );
  }, [compareByGroups, isCompareBySegmented]);

  const activeCompareByTab =
    compareByTabs.find(({ value }) => value === selectedCompareByTab)?.value ||
    compareByTabs[0]?.value;

  // resolve the active tab (keyed by group id) back to the group name the backend nests data under
  const activeGroupName = compareByGroups?.find(
    ({ id, name }) => String(id ?? name) === activeCompareByTab
  )?.name;
  const displayData = isCompareBySegmented ? data.dataByCompareBy[activeGroupName] : data;
  const tableToDisplay = isDisplayAsPercent ? displayData?.tablePct : displayData?.tableCount;

  // compare-by segments with a monadic slice, in display order; the order sets each segment's letter
  const monadicSegmentNames = useMemo(
    () =>
      isMonadicSectionQuestion && isCompareBySegmented
        ? (compareByGroups ?? [])
            .map(({ name }) => name)
            .filter((name) => name != null && data.dataByCompareBy[name]?.tableCount)
        : [],
    [compareByGroups, data?.dataByCompareBy, isCompareBySegmented, isMonadicSectionQuestion]
  );

  // Monadic-section grids letter compare-by segments against each other across their slices.
  // Within a slice the compare-by column holds concepts, which are never compared: sequential
  // monadic respondents see every concept, so those samples overlap.
  const significance = useMemo(() => {
    if (!significanceEnabled || !showSignificanceLetters) return null;
    if (isMonadicSectionQuestion) {
      return computeMonadicGridSignificance({
        activeSegmentName: activeGroupName,
        alpha: significanceAlpha,
        dataByCompareBy: data?.dataByCompareBy,
        segmentNames: monadicSegmentNames,
      });
    }
    return computeGridSignificance(displayData?.tableCount, significanceAlpha);
  }, [
    significanceEnabled,
    showSignificanceLetters,
    significanceAlpha,
    isMonadicSectionQuestion,
    activeGroupName,
    data?.dataByCompareBy,
    monadicSegmentNames,
    displayData?.tableCount,
  ]);

  // monadic tables show concepts in the compare-by column, so the segment letters go on the tabs
  const compareByTabOptions = useMemo(() => {
    if (!significance || !isMonadicSectionQuestion) return compareByTabs;

    return compareByTabs.map((tab) => {
      const name = compareByGroups?.find(({ id, name }) => String(id ?? name) === tab.value)?.name;
      const segmentIndex = monadicSegmentNames.indexOf(name);
      return segmentIndex === -1
        ? tab
        : { ...tab, label: `${tab.label} (${indexToColumnLetter(segmentIndex)})` };
    });
  }, [compareByGroups, compareByTabs, isMonadicSectionQuestion, monadicSegmentNames, significance]);

  const tableColumns = useMemo(
    () =>
      significance
        ? applyGridSignificanceColumns({ columns: tableToDisplay?.columns, significance })
        : tableToDisplay?.columns,
    [significance, tableToDisplay?.columns]
  );

  const colors = useMemo(() => {
    if (!displayData) return [];
    const colors = getGridColorsForCount(displayData.segments?.[0]?.chartCount?.series?.length);
    return areColorsReversed ? colors.toReversed() : colors;
  }, [areColorsReversed, displayData]);

  useEffect(() => {
    setSelectedCompareByTab((currentTab) =>
      compareByTabs.some(({ value }) => value === currentTab) ? currentTab : compareByTabs[0]?.value
    );
  }, [compareByTabs]);

  if (!displayData) {
    return (
      <Layout.Flex.Column className={styles['grid']} gap="x-large">
        {compareByTabs.length > 0 && (
          <Tabs
            aria-label="Compare by group"
            onChange={setSelectedCompareByTab}
            options={compareByTabOptions}
            value={activeCompareByTab}
          />
        )}
        <NoDataPlaceholder />
      </Layout.Flex.Column>
    );
  }

  return (
    <Layout.Flex.Column className={styles['grid']} gap="x-large">
      {compareByTabs.length > 0 && (
        <Tabs
          aria-label="Compare by group"
          onChange={setSelectedCompareByTab}
          options={compareByTabOptions}
          value={activeCompareByTab}
        />
      )}
      <Layout.Flex.Column gap="small">
        <div className={styles['grid-chart-controls']}>
          <h4 className={styles['grid-label']}>{`Answer Options by ${
            isDisplayAsPercent ? DISPLAY_AS.PERCENT.label : DISPLAY_AS.COUNT.label
          }`}</h4>
          <div>
            <Button
              icon={
                <ArrowsLeftRightIcon
                  className={classNames(styles['grid-color-invert-icon'], {
                    [styles['is-reversed']]: areColorsReversed,
                  })}
                />
              }
              onClick={() => setAreColorsReversed((prev) => !prev)}
              text="Invert Colors"
              variant="secondary"
            />
          </div>
        </div>

        {displayData?.segments?.map((segment, index) => {
          const chartToDisplay = segment[chartKeyToDisplay];
          const isLastSegment = index === displayData.segments.length - 1;

          const barHeight = 16;
          const heightForLegend = 32;
          const minHeightForLabel = 20;
          const minHeightForTooltip = 50;
          const paddingBetweenBars = (minHeightForLabel - barHeight) / 2;
          const chartHeight =
            chartToDisplay.xAxis.categories.length * (minHeightForLabel + paddingBetweenBars);
          return (
            <BarStackChart
              chart={{
                height: isLastSegment
                  ? Math.max(chartHeight + heightForLegend, minHeightForTooltip + heightForLegend)
                  : Math.max(chartHeight, minHeightForTooltip),
                marginRight: 15,
                spacing: [0, 0, 0, 0],
              }}
              chartTitle={segment.name}
              className={styles['grid-chart']}
              colors={colors}
              hasLegend={false}
              key={segment.id}
              plotOptions={{
                series: {
                  groupPadding: 0,
                  pointPadding: 0,
                  pointWidth: barHeight,
                },
              }}
              series={chartToDisplay.series}
              tooltip={{
                formatter: function () {
                  return tooltipFormatter({
                    data: this,
                    format: segment[chartKeyToDisplay]?.format,
                  });
                },
              }}
              xAxis={{
                ...segment[chartKeyToDisplay]?.xAxis,
                labels: {
                  align: 'left',
                  formatter: function () {
                    const isOverall = selectedCompareBy?.value === 'overall';
                    const color = isOverall
                      ? compareByGroups?.[0]?.color
                      : compareByGroups?.find(({ id, name }) => [id, name].includes(this.value))
                          ?.color;
                    const escapeHtml = (value) =>
                      String(value)
                        .replace(/&/g, '&amp;')
                        .replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;')
                        .replace(/"/g, '&quot;')
                        .replace(/'/g, '&#39;');

                    // monadic-section grids show the payload category (like every other response);
                    // a normal grid keeps the single "Overall" label
                    const rawText =
                      isOverall && !isMonadicSectionQuestion
                        ? compareByGroups?.[0]?.name
                        : truncateTextWithEllipsis({ length: 25, text: this.value });
                    const text = escapeHtml(rawText);
                    // monadic-section grid labels are concepts, not compare-by groups, so drop the dot
                    const dot = isMonadicSectionQuestion
                      ? ''
                      : `<span class="${styles['grid-xaxis-dot']}" style="background:${color}"></span>`;
                    return `<div class="${styles['grid-xaxis-label']}">${dot}${text}</div>`;
                  },
                  reserveSpace: true,
                  useHTML: true,
                },
              }}
              yAxis={{
                labels: {
                  formatter: function () {
                    return isLastSegment
                      ? formatData({
                          format: chartToDisplay.format,
                          value: this.value,
                        })
                      : '';
                  },
                },
                max: isDisplayAsPercent ? 1 : undefined,
                tickAmount: isDisplayAsPercent ? 11 : undefined,
                visible: true,
              }}
            />
          );
        })}
        <Legend
          className={styles['grid-legend']}
          isHorizontal
          items={displayData?.segments?.[0]?.chartCount?.series.map(({ name }, index) => ({
            color: colors[index],
            name,
          }))}
        />
      </Layout.Flex.Column>
      <hr />
      <GridTable
        actionButtons={
          significanceAvailable
            ? [
                <SignificanceControls
                  alpha={significanceAlpha}
                  inputId={`show-significance-${question.id}`}
                  key="significance-controls"
                  onChange={handleSignificanceChange}
                />,
              ]
            : []
        }
        columns={tableColumns}
        compareByGroups={compareByGroups}
        data={tableToDisplay?.data}
        displayAs={displayAs}
        isAskWhySurvey={isAskWhySurvey}
        language={language}
        onTop2BoxApplied={onTop2BoxApplied}
        question={question}
        surveyId={surveyId}
      />
      {significance && (
        <p className={styles['grid-significance-legend']}>
          A letter and green highlight mark a segment significantly higher than the lettered
          segment(s) for that answer option{isMonadicSectionQuestion ? ' and concept' : ''} at{' '}
          {Math.round((1 - significanceAlpha) * 100)}% confidence.
          {isMonadicSectionQuestion ? ' Segment letters are shown on the tabs.' : ''} Segments with
          fewer than {statThresholds.lowBaseSize} respondents aren&apos;t tested.
        </p>
      )}
    </Layout.Flex.Column>
  );
};

Grid.propTypes = {
  compareByGroups: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.object,
  displayAs: PropTypes.string,
  initialSignificanceAlpha: PropTypes.number,
  isAskWhySurvey: PropTypes.bool,
  language: PropTypes.string,
  onSignificanceChange: PropTypes.func,
  onTop2BoxApplied: PropTypes.func,
  question: PropTypes.object,
  selectedCompareBy: PropTypes.object,
  significanceAvailable: PropTypes.bool,
  surveyId: PropTypes.number,
};

export default Grid;
